import React, { useState } from 'react';
import * as Yup from 'yup';
import { Formik } from 'formik';
import {
  Alert,
  Box,
  Button,
  Collapse,
  Grid,
  Link,
  Typography,
} from '@mui/material';
import { LockOutlined, EmailOutlined } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import useMounted from '../../hooks/useMounted';
import StyledInput from '../inputs/StyledInput';
import { LoadingButton } from '@mui/lab';
import { Tracker } from '../../utils/tracker';
import { loginFields } from './fields';
import { useFeatureFlag } from '../../hooks';
import LoginJWTOld from './LoginJWTOld';

const LoginJWT = (props) => {
  const mounted = useMounted();
  const { login, sendMagicLink } = useAuth();
  const tracker = Tracker.getInstance();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('password'); // 'password' or 'magiclink'
  const { isActive } = useFeatureFlag();
  const [isMagicLinkFlagActive, setIsMagicLinkFlagActive] = useState(false);
  React.useEffect(() => {
    isActive('magic_link', 'Railway').then((active) => {
      setIsMagicLinkFlagActive(active);
    });
  }, []);
  if (!isMagicLinkFlagActive) return <LoginJWTOld {...props} />;
  return (
    <Formik
      initialValues={{
        email: '',
        password: '',
        submit: null,
      }}
      validationSchema={Yup.object().shape({
        email: Yup.string()
          .email('Must be a valid email')
          .max(80)
          .required('Email is required'),
        password: Yup.string().max(255).required('Password is required'),
      })}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        try {
          await login(values.email, values.password);
          tracker.identify(values.email);
          tracker.track(Tracker.EVENTS.LOGIN);

          if (mounted.current) {
            setStatus({ success: true });
            setSubmitting(false);
          }
        } catch (err) {
          if (mounted.current) {
            setStatus({ success: false });
            setErrors(err.data ? { ...err.data } : { submit: err.message });
            setSubmitting(false);
          }
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
        setFieldError,
        isValid,
      }) => (
        <Box
          component="form"
          noValidate
          onSubmit={handleSubmit}
          mb={0}
          {...props}
        >
          <Box sx={{ mb: 3 }}>
            <Collapse in={!!errors.submit || props.customError} sx={{ my: 2 }}>
              <Alert
                severity="error"
                variant="filled"
                icon={false}
                sx={{ px: 1, py: 0.5, borderRadius: 0.5 }}
              >
                <Typography variant="body1">
                  {errors.submit ?? props.customError}
                </Typography>
              </Alert>
            </Collapse>
            <Typography
              color="textPrimary"
              variant="h3"
              sx={{ textAlign: 'center', mb: 2 }}
            >
              Log In
            </Typography>
            <Typography
              color="textSecondary"
              gutterBottom
              variant="body1"
              sx={{ textAlign: 'center', mb: 3 }}
            >
              Enter your email and select your log in method
            </Typography>

            {/* Tab Buttons */}
            <Box
              sx={{
                display: 'flex',
                mb: 3,
                borderRadius: 1,
                overflow: 'hidden',
                border: '1px solid #e0e0e0',
              }}
            >
              <Button
                fullWidth
                variant="text"
                startIcon={<LockOutlined />}
                onClick={() => setActiveTab('password')}
                sx={{
                  backgroundColor:
                    activeTab === 'password' ? '#f4f4f5' : '#ffffff',
                  color: 'text.primary',
                  borderRadius: 0,
                  py: 1.5,
                  borderRight: '1px solid #e0e0e0',
                  '&:hover': {
                    backgroundColor:
                      activeTab === 'password' ? '#e5e5e5' : '#f9f9f9',
                  },
                }}
              >
                Password
              </Button>
              <Button
                fullWidth
                variant="text"
                startIcon={<EmailOutlined />}
                onClick={() => setActiveTab('magiclink')}
                sx={{
                  backgroundColor:
                    activeTab === 'magiclink' ? '#f4f4f5' : '#ffffff',
                  color: 'text.primary',
                  borderRadius: 0,
                  py: 1.5,
                  '&:hover': {
                    backgroundColor:
                      activeTab === 'magiclink' ? '#e5e5e5' : '#f9f9f9',
                  },
                }}
              >
                Login with Link
              </Button>
            </Box>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <StyledInput
                error={Boolean(touched.email && errors.email)}
                fullWidth
                helperText={touched.email && errors.email}
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.email}
                variant="outlined"
                id={loginFields.email.id}
                label={loginFields.email.label}
                name={loginFields.email.name}
                data-testid={loginFields.email.testId}
                type={loginFields.email.type}
              />
            </Grid>

            {/* Magic Link Notice - only show when Magic Link tab is active */}
            {activeTab === 'magiclink' && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    backgroundColor: '#e8e5ff',
                    border: '1px solid #d1c7ff',
                    borderRadius: 1,
                    p: 2,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                  }}
                >
                  <EmailOutlined
                    sx={{
                      color: '#6366f1',
                      fontSize: 20,
                      mt: 0.25,
                    }}
                  />
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: '#6366f1',
                        fontWeight: 600,
                        mb: 0.5,
                      }}
                    >
                      Email Link Login
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#6366f1',
                        lineHeight: 1.4,
                      }}
                    >
                      We&apos;ll send you a secure login link via email. Click
                      the link to sign in instantly without a password.
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}

            {activeTab === 'password' && (
              <Grid item xs={12}>
                <StyledInput
                  error={Boolean(touched.password && errors.password)}
                  fullWidth
                  helperText={touched.password && errors.password}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.password}
                  variant="outlined"
                  id={loginFields.password.id}
                  label={loginFields.password.label}
                  name={loginFields.password.name}
                  data-testid={loginFields.password.testId}
                  type={loginFields.password.type}
                />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mt: 1,
                    mb: 2,
                  }}
                >
                  <Link
                    component={RouterLink}
                    to="/authentication/password-recovery"
                    variant="body2"
                  >
                    Forgot password?
                  </Link>
                </Box>
              </Grid>
            )}
          </Grid>
          <Box sx={{ py: 2 }}>
            {activeTab === 'password' ? (
              <Grid container columnSpacing={5}>
                <Grid item xs={6}>
                  <RouterLink
                    to="/authentication/register"
                    style={{ textDecoration: 'none' }}
                  >
                    <Button
                      color="primary"
                      disabled={isSubmitting}
                      fullWidth
                      type="button"
                      variant="text"
                      sx={{ px: 1 }}
                      tabIndex={3}
                    >
                      Register a new account
                    </Button>
                  </RouterLink>
                </Grid>
                <Grid
                  item
                  xs={6}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <LoadingButton
                    color="primary"
                    loading={isSubmitting}
                    disabled={isSubmitting || !isValid}
                    fullWidth
                    type="submit"
                    data-testid="login"
                    variant="contained"
                    tabIndex={2}
                  >
                    Log In
                  </LoadingButton>
                </Grid>
              </Grid>
            ) : (
              <Grid container columnSpacing={5}>
                <Grid item xs={6}>
                  <RouterLink
                    to="/authentication/register"
                    style={{ textDecoration: 'none' }}
                  >
                    <Button
                      color="primary"
                      disabled={isSubmitting}
                      fullWidth
                      type="button"
                      variant="text"
                      sx={{ px: 1 }}
                      tabIndex={3}
                    >
                      Register a new account
                    </Button>
                  </RouterLink>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    color="primary"
                    fullWidth
                    variant="contained"
                    onClick={async () => {
                      try {
                        await sendMagicLink(values.email);
                        tracker.track(Tracker.EVENTS.REQUEST_LOGIN_MAGIC_LINK);
                        navigate('/authentication/check-email', {
                          state: { email: values.email },
                        });
                      } catch (err) {
                        if (mounted.current) {
                          setFieldError(
                            'submit',
                            err.data ? err.data.message : err.message
                          );
                        }
                      }
                    }}
                    disabled={
                      !values.email || Boolean(touched.email && errors.email)
                    }
                  >
                    Send Login Link
                  </Button>
                </Grid>
              </Grid>
            )}
          </Box>
        </Box>
      )}
    </Formik>
  );
};

export default LoginJWT;
